import { Component, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { EnrichedAuditTrailIbPrivateOpportunitiesDistributionItem } from './autit-trail-ib-private-opportunities-distribution-item.model';
import moment from 'moment';
import {
  DetailRowService,
  FilterService,
  GridComponent,
  GridModule,
  PageService,
  SearchService,
  ToolbarService
} from '@syncfusion/ej2-angular-grids';
import { ToastComponent } from '../../shared/toast.component';
import { AuditTrailIbPrivateOpportunitiesDistributionService } from './audit-trail-ib-private-opportunities-distribution.service';
import { NGXLogger } from 'ngx-logger';
import { SpinnerComponent } from '../../shared/spinner.component';
import { DateTimePickerModule } from '@syncfusion/ej2-angular-calendars';
import { AsyncPipe, NgClass } from '@angular/common';
import { CigpStaffAvatarCardComponent } from '../../cigp-staff/cigp-staff-avatar-card.component';

@Component({
  selector: 'eaa-audit-trail-ib-private-opportunities-distribution',
  providers: [DetailRowService, FilterService, PageService, SearchService, ToolbarService],
  imports: [
    AsyncPipe,
    CigpStaffAvatarCardComponent,
    DateTimePickerModule,
    GridModule,
    NgClass,
    SpinnerComponent,
    ToastComponent
  ],
  template: `
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center gap-3">
          <div>
            <h5 class="card-title">IB PRIVATE OPPORTUNITIES DISTRIBUTION</h5>
            <h6 class="card-subtitle text-body-secondary">Audit Trail</h6>
          </div>
          @if (loadingPendingCount) {
            <div class="d-flex align-items-center gap-2">
              <eaa-spinner [diameter]="16" />
              <span class="text-muted">Loading files pending distribution...</span>
            </div>
          } @else {
            <span
              class="badge fs-6 px-3 py-2"
              [ngClass]="{
                'text-success-emphasis bg-success-subtle': pendingFilesCount > 0,
                'text-secondary-emphasis bg-secondary-subtle': pendingFilesCount === 0
              }"
            >
              {{ pendingFilesCount }} file{{ pendingFilesCount !== 1 ? 's' : '' }} pending distribution
            </span>
          }
          <button
            type="button"
            class="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
            [disabled]="triggeringDistribution || pendingFilesCount === 0"
            (click)="triggerDistribution()"
          >
            @if (triggeringDistribution) {
              <eaa-spinner />
              <span>DISTRIBUTING...</span>
            } @else {
              <span>START DISTRIBUTION</span>
            }
          </button>
        </div>
        <div class="d-flex align-items-center gap-3">
          <ejs-datetimepicker
            [showClearButton]="false"
            [enabled]="!searching"
            (change)="dateRangeSelected()"
            [max]="today"
            placeholder="Start"
            floatLabelType="Auto"
            [(value)]="start"
            format="dd/MM/yyyy HH:mm:ss z"
            width="260px"
          ></ejs-datetimepicker>
          <ejs-datetimepicker
            [showClearButton]="false"
            [enabled]="!searching"
            (change)="dateRangeSelected()"
            [max]="today"
            placeholder="End"
            floatLabelType="Auto"
            [(value)]="end"
            format="dd/MM/yyyy HH:mm:ss z"
            width="260px"
          ></ejs-datetimepicker>
        </div>
      </div>
      <div class="card-body p-0">
        <ejs-grid
          #grdAuditTrailMonthlyStatementsDistributionItems
          id="grdAuditTrailMonthlyStatementsDistributionItems"
          [dataSource]="items$ | async"
          [allowPaging]="true"
          [pageSettings]="{ pageSize: 50, pageSizes: ['10', '20', '50', '100', '250', 'All'] }"
          [allowFiltering]="true"
          [filterSettings]="{ enableCaseSensitivity: false, ignoreAccent: true, type: 'Excel' }"
          [allowTextWrap]="true"
          [toolbar]="['Search']"
          [allowSelection]="false"
        >
          <e-columns>
            <e-column type="checkbox" width="24px"></e-column>
            <e-column field="id" headerText="#" textAlign="Right" width="80px" format="N0" [isPrimaryKey]="true">
            </e-column>
            <e-column
              field="timestamp"
              headerText="Timestamp"
              type="datetime"
              format="dd/MM/yyyy HH:mm:ss zz"
              width="200px"
            ></e-column>
            <e-column headerText="Security" width="300px">
              <ng-template #template let-data>
                <div class="d-flex flex-column gap-1">
                  <span>{{ data.securityName }}</span>
                  <i
                    ><small>{{ data.securityCode }}</small></i
                  >
                </div>
              </ng-template>
            </e-column>
            <e-column field="portfolio" headerText="Portfolio" width="180px" />
            <e-column field="result" headerText="Result" width="140px">
              <ng-template #template let-data>
                <span
                  class="badge"
                  [ngClass]="{
                    'text-success-emphasis': data.result === 'Success',
                    'bg-success-subtle': data.result === 'Success',
                    'text-warning-emphasis': data.result === 'Skipped',
                    'bg-warning-subtle': data.result === 'Skipped',
                    'text-danger-emphasis': data.result === 'Failed',
                    'bg-danger-subtle': data.result === 'Failed'
                  }"
                >
                  {{ data.result }}
                </span>
              </ng-template>
            </e-column>
            <e-column field="clientsSuccessCount" headerText="OK Clients" format="N" width="130px"></e-column>
            <e-column field="clientsFailedCount" headerText="Failed Clients" format="N" width="150px"></e-column>
            <e-column field="staffsSuccessCount" headerText="OK Staffs" format="N" width="130px"></e-column>
            <e-column field="staffsFailedCount" headerText="Failed Staffs" format="N" width="150px"></e-column>
            <e-column field="fileName" headerText="File"></e-column>
          </e-columns>
          <ng-template #detailTemplate let-data>
            <div class="d-flex flex-column gap-3 p-3">
              <div>
                <h6 class="card-title">CLIENTS</h6>
                <div class="d-flex flex-wrap gap-2">
                  @if (data.distributedToClients.length > 0) {
                    @for (item of data.distributedToClients; track $index) {
                      <span
                        class="badge"
                        [ngClass]="{
                          'text-success-emphasis': item.success,
                          'bg-success-subtle': item.success,
                          'text-danger-emphasis': !item.success,
                          'bg-danger-subtle': !item.success
                        }"
                        [title]="item.email"
                      >
                        {{ item.displayName }}
                      </span>
                    }
                  } @else {
                    <p class="m-0">-</p>
                  }
                </div>
              </div>
              <div>
                <h6 class="card-title">CIGP STAFF</h6>
                <div class="d-flex flex-wrap gap-2">
                  @if (data.distributedToCigpStaffs.length > 0) {
                    @for (item of data.distributedToCigpStaffs; track $index) {
                      <span
                        class="badge"
                        [ngClass]="{
                          'text-success-emphasis': item.success,
                          'bg-success-subtle': item.success,
                          'text-danger-emphasis': !item.success,
                          'bg-danger-subtle': !item.success
                        }"
                      >
                        <eaa-cigp-staff-avatar-card [user]="item" [showInitials]="true"></eaa-cigp-staff-avatar-card>
                      </span>
                    }
                  } @else {
                    <p class="m-0">-</p>
                  }
                </div>
              </div>
              <div>
                <h6 class="card-title">NOTES</h6>
                <p class="m-0">{{ data.notes }}</p>
              </div>
            </div>
          </ng-template>
        </ejs-grid>
      </div>
    </div>

    <eaa-toast #toastTriggerResult [showCloseButton]="true">
      <div id="title">TRIGGER DISTRIBUTION</div>
      <div id="content">
        {{ triggerResultMessage }}
      </div>
    </eaa-toast>
  `
})
export class AuditTrailIbPrivateOpportunitiesDistributionComponent implements OnInit {
  public items$: Observable<EnrichedAuditTrailIbPrivateOpportunitiesDistributionItem[]> | null = null;
  public today = new Date();
  public searching = false;
  public triggeringDistribution = false;
  public loadingPendingCount = false;
  public pendingFilesCount = 0;
  public start: Date = moment().subtract(20, 'days').toDate();
  public end: Date = new Date();
  public processingMessage = '';
  public triggerResultMessage = '';

  @ViewChild('grdAuditTrailIbPrivateOpportunitiesDistributionItems')
  public grid?: GridComponent;

  @ViewChild('toastTriggerResult')
  public toastTriggerResult?: ToastComponent;

  constructor(
    private auditTrailIbPrivateOpportunitiesDistributionService: AuditTrailIbPrivateOpportunitiesDistributionService,
    private logger: NGXLogger
  ) {}

  public ngOnInit(): void {
    this.setAuditTrailItems();
    this.loadPendingFilesCount();
  }

  public dateRangeSelected(): void {
    this.searching = true;

    this.setAuditTrailItems();

    setTimeout(() => {
      this.searching = false;
    }, 500);
  }

  public loadPendingFilesCount(): void {
    this.loadingPendingCount = true;

    this.auditTrailIbPrivateOpportunitiesDistributionService
      .countIbPrivateOpportunitiesFilesPendingDistribution$()
      .subscribe({
        next: (response) => {
          this.loadingPendingCount = false;
          if (response.count !== undefined) {
            this.pendingFilesCount = response.count;
          } else if (response.error) {
            this.logger.error('Error loading pending files count:', response.error);
            this.pendingFilesCount = 0;
          }
        },
        error: (error) => {
          this.logger.error('Failed to load pending files count', error);
          this.loadingPendingCount = false;
          this.pendingFilesCount = 0;
        }
      });
  }

  public triggerDistribution(): void {
    this.triggeringDistribution = true;
    this.logger.log('Triggering IB private opportunities distribution...');

    this.auditTrailIbPrivateOpportunitiesDistributionService.triggerIbPrivateOpportunitiesDistribution$().subscribe({
      next: (response) => {
        this.logger.log('IB private opportunities distribution triggered successfully', response);
        this.triggeringDistribution = false;
        this.triggerResultMessage = response.message;
        this.toastTriggerResult?.show();
        // Refresh the audit trail items and pending count after successful trigger
        this.setAuditTrailItems();
        this.loadPendingFilesCount();
      },
      error: (error) => {
        this.logger.error('Failed to trigger IB private opportunities distribution', error);
        this.triggeringDistribution = false;
      }
    });
  }

  private setAuditTrailItems() {
    this.items$ =
      this.auditTrailIbPrivateOpportunitiesDistributionService.getAuditTrailIbPrivateOpportunitiesDistributionItems$(
        this.start,
        this.end,
        null,
        null
      );
  }
}
